import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError, formatDate, downloadAuthenticatedPdf } from "@/lib/utils-app";
import AdminUserModerationSection from "@/components/AdminUserModerationSection";
import { KeyIcon as Key, CopyIcon as Copy, GiftIcon as Gift, ClockIcon as Clock, CreditCardIcon as CreditCard, ReceiptIcon as Receipt, EnvelopeSimpleIcon as EnvelopeSimple, CheckCircleIcon as CheckCircle, ArrowSquareOutIcon as ArrowSquareOut, EyeIcon as Eye, UserSwitchIcon as UserSwitch, DownloadSimpleIcon as DownloadSimple } from "@phosphor-icons/react";

/**
 * AdminUserDetailsDialog — drop-in support panel for one user.
 *
 * Surfaces every assistance action the admin can take on a user:
 *   - Send / copy a password reset link (single-use, 60 min)
 *   - View active + expired subscriptions
 *   - Extend a subscription by N days (goodwill comp)
 *   - Grant a comp subscription (no Stripe)
 *   - Browse recent payment_transactions with deep links to Stripe dashboard
 *
 * Everything routes through audit-logged admin endpoints.
 */
export default function AdminUserDetailsDialog({ userId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [consents, setConsents] = useState(null);
  const [consentsLoading, setConsentsLoading] = useState(false);

  const refresh = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/data/users/${userId}/details`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load user details."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && userId) {
      setResetResult(null);
      setGrantOpen(false);
      refresh();
    }
  }, [open, userId]);

  const issueResetLink = async (sendEmail) => {
    setResetting(true);
    try {
      const r = await api.post(`/admin/data/users/${userId}/reset-password`, {
        send_email: sendEmail,
      });
      setResetResult(r.data);
      toast.success(
        r.data.kind === "oauth_only"
          ? "User is Google-only — sent the OAuth notice."
          : sendEmail
          ? "Reset email sent + link copied below."
          : "Reset link generated. Copy below to share manually.",
      );
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't generate reset link."));
    } finally {
      setResetting(false);
    }
  };

  const copyLink = () => {
    if (resetResult?.reset_link) {
      navigator.clipboard?.writeText(resetResult.reset_link);
      toast.success("Reset link copied to clipboard.");
    }
  };

  const loadSnapshot = async () => {
    if (!userId) return;
    setSnapshotLoading(true);
    try {
      const r = await api.get(`/admin/data/users/${userId}/snapshot`);
      setSnapshot(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load user snapshot."));
    } finally {
      setSnapshotLoading(false);
    }
  };

  const loadConsents = async () => {
    if (!userId) return;
    setConsentsLoading(true);
    try {
      const r = await api.get(`/admin/data/users/${userId}/consents`);
      setConsents(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load consents."));
    } finally {
      setConsentsLoading(false);
    }
  };

  const startImpersonation = async () => {
    const userEmail = data?.user?.email || "this user";
    const ok = window.confirm(
      `Sign in as ${userEmail}?\n\n` +
      `• The user will be notified by inbox + email (legal requirement).\n` +
      `• Your session expires in 60 minutes.\n` +
      `• Every action you take is audit-logged.\n` +
      `• A persistent banner will show across the top.\n\n` +
      `Click OK to continue.`,
    );
    if (!ok) return;
    setImpersonating(true);
    try {
      const r = await api.post(`/admin/data/users/${userId}/impersonate`);
      // Stash the admin's current token so we can restore it on "Stop".
      const adminToken = localStorage.getItem("token");
      if (adminToken) localStorage.setItem("admin_token_before_impersonation", adminToken);
      localStorage.setItem("token", r.data.token);
      localStorage.setItem(
        "impersonation_target",
        JSON.stringify({
          email: r.data.target_user?.email,
          name: r.data.target_user?.name,
          expires_at: new Date(Date.now() + r.data.expires_in * 1000).toISOString(),
        }),
      );
      // Hard reload so every component picks up the new token cleanly.
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't start impersonation."));
      setImpersonating(false);
    }
  };

  if (!open) return null;
  const u = data?.user;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-user-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl tracking-tight">
            {u ? u.name || u.email : "User"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            {u?.email}
            {u?.role === "admin" && <Badge className="ml-2 bg-gold text-ink">Admin</Badge>}
            {u?.suspended && <Badge className="ml-2 bg-crimson text-white">Suspended</Badge>}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <p className="text-[12.5px] text-ink/55 py-8 text-center">Loading…</p>
        )}

        {!loading && data && (
          <div className="space-y-6 text-[13px]">
            <AdminUserModerationSection
              userId={userId}
              notes={data.admin_notes || []}
            />

            {/* Reset password */}
            <section data-testid="admin-user-detail-reset">
              <div className="flex items-center gap-2 mb-2">
                <Key size={14} weight="duotone" className="text-ink/65" />
                <h3 className="font-semibold text-ink text-[14px]">Password help</h3>
              </div>
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-4">
                <p className="text-[12px] text-ink/65 leading-snug mb-3">
                  Issues a fresh one-time reset link (60 min TTL, single-use, audit-logged).
                  Use this when a customer can't get their reset email to arrive.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    disabled={resetting}
                    onClick={() => issueResetLink(true)}
                    className="rounded-full h-8 text-[12px] bg-ink hover:bg-crimson text-white"
                    data-testid="admin-user-reset-email"
                  >
                    <EnvelopeSimple size={12} weight="duotone" className="mr-1" />
                    Send reset email + show link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={resetting}
                    onClick={() => issueResetLink(false)}
                    className="rounded-full h-8 text-[12px] border-stone-300"
                    data-testid="admin-user-reset-link-only"
                  >
                    <Copy size={12} weight="duotone" className="mr-1" />
                    Just generate a link (no email)
                  </Button>
                </div>
                {resetResult && resetResult.reset_link && (
                  <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-[11.5px]">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="font-semibold text-emerald-900 inline-flex items-center gap-1">
                        <CheckCircle size={12} weight="fill" />
                        Link ready · expires{" "}
                        {resetResult.expires_at ? formatDate(resetResult.expires_at) : ""}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={copyLink}
                        className="h-6 text-[10.5px] rounded-full border-emerald-300"
                      >
                        <Copy size={10} className="mr-1" />
                        Copy
                      </Button>
                    </div>
                    <code className="block bg-white px-2 py-1.5 rounded border border-emerald-200 text-[11px] break-all font-tabular text-ink/85">
                      {resetResult.reset_link}
                    </code>
                  </div>
                )}
                {resetResult && resetResult.kind === "oauth_only" && (
                  <p className="mt-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    This account signs in with Google. There's no password to reset —
                    sent them a friendly "use Google sign-in" email instead.
                  </p>
                )}
                {data.recent_reset_attempts?.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-[11px] text-ink/55 cursor-pointer">
                      {data.recent_reset_attempts.length} recent reset attempt(s)
                    </summary>
                    <ul className="mt-1.5 text-[11px] text-ink/65 space-y-0.5 pl-2">
                      {data.recent_reset_attempts.map((r, i) => (
                        <li key={i} className="font-tabular">
                          {formatDate(r.created_at)} —{" "}
                          {r.used_at ? (
                            <span className="text-emerald-700">used {formatDate(r.used_at)}</span>
                          ) : (
                            <span className="text-ink/45">unused</span>
                          )}
                          {r.issued_by_admin_id && (
                            <span className="ml-1 text-amber-700">(admin-issued)</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </section>

            {/* Support access: read-only snapshot + full impersonation */}
            <section data-testid="admin-user-detail-support-access">
              <div className="flex items-center gap-2 mb-2">
                <Eye size={14} weight="duotone" className="text-ink/65" />
                <h3 className="font-semibold text-ink text-[14px]">Support access</h3>
              </div>
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-4">
                <p className="text-[12px] text-ink/65 leading-snug mb-3">
                  Diagnose customer issues without asking them to describe the screen.
                  Start with <strong>View snapshot</strong> (read-only). Escalate to{" "}
                  <strong>Sign in as</strong> only if you need to reproduce an interactive flow —
                  the user gets an email + inbox notification when you do.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={snapshotLoading}
                    onClick={loadSnapshot}
                    className="rounded-full h-8 text-[12px] border-stone-300"
                    data-testid="admin-user-view-snapshot"
                  >
                    <Eye size={12} weight="duotone" className="mr-1" />
                    {snapshotLoading ? "Loading…" : snapshot ? "Refresh snapshot" : "View snapshot"}
                  </Button>
                  <Button
                    size="sm"
                    disabled={impersonating}
                    onClick={startImpersonation}
                    className="rounded-full h-8 text-[12px] bg-ink hover:bg-crimson text-white"
                    data-testid="admin-user-impersonate"
                    title="Sign in as this user. They get notified by email + inbox."
                  >
                    <UserSwitch size={12} weight="duotone" className="mr-1" />
                    {impersonating ? "Starting…" : "Sign in as"}
                  </Button>
                </div>
                {snapshot && (
                  <div className="mt-4 grid grid-cols-2 gap-3 text-[11.5px]" data-testid="admin-user-snapshot">
                    <SnapStat label="Active subs" value={snapshot.active_subscriptions?.length || 0} />
                    <SnapStat label="Dogs" value={snapshot.dogs?.length || 0} />
                    <SnapStat label="Litters" value={snapshot.litters?.length || 0} />
                    <SnapStat label="Recent payments" value={snapshot.recent_payments?.length || 0} />
                    <SnapStat label="Recent messages" value={snapshot.recent_messages?.length || 0} />
                    <SnapStat label="Admin actions on file" value={snapshot.audit_log?.length || 0} />
                    {snapshot.dogs?.length > 0 && (
                      <div className="col-span-2 mt-1">
                        <p className="text-[11px] uppercase tracking-[0.1em] text-ink/55 font-semibold mb-1">Dogs</p>
                        <ul className="space-y-0.5 text-ink/80">
                          {snapshot.dogs.slice(0, 6).map((d) => (
                            <li key={d.id}>
                              · <strong>{d.name}</strong>{d.breed ? ` (${d.breed})` : ""}
                              {d.approval_status && d.approval_status !== "approved" && (
                                <span className="ml-1 text-amber-700">[{d.approval_status}]</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {snapshot.recent_payments?.length > 0 && (
                      <div className="col-span-2 mt-1">
                        <p className="text-[11px] uppercase tracking-[0.1em] text-ink/55 font-semibold mb-1">Recent payments</p>
                        <ul className="space-y-0.5 text-ink/80">
                          {snapshot.recent_payments.slice(0, 5).map((p) => (
                            <li key={p.id || p.session_id}>
                              · ${(p.amount_usd || 0).toFixed(2)} · {p.package_id || p.kind} ·{" "}
                              <span className={p.payment_status === "paid" ? "text-emerald-700" : "text-crimson"}>
                                {p.payment_status || "unknown"}
                              </span>
                              {p.fulfillment_blocked && (
                                <span className="ml-1 text-crimson font-semibold">⚠ unfulfilled</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Consents audit trail — cookies, Terms, Privacy, marketing emails. */}
            <section data-testid="admin-user-detail-consents">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} weight="duotone" className="text-ink/65" />
                  <h3 className="font-semibold text-ink text-[14px]">
                    Consents & acceptance
                  </h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadConsents}
                  disabled={consentsLoading}
                  className="rounded-full h-7 text-[11px] border-stone-300"
                  data-testid="admin-user-load-consents"
                >
                  {consentsLoading ? "Loading…" : consents ? "Refresh" : "Show history"}
                </Button>
              </div>
              {consents && (
                <div className="rounded-xl bg-stone-50 border border-stone-200 p-3" data-testid="admin-user-consents-panel">
                  {(consents.consents || []).length === 0 ? (
                    <p className="text-[12px] text-ink/65 leading-snug">
                      No server-side consent records exist for this user yet. New
                      cookie and policy choices made from this point on will be
                      captured here.
                    </p>
                  ) : (
                    <ul className="space-y-1.5 text-[11.5px]">
                      {consents.consents.map((c) => (
                        <li
                          key={c.id}
                          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5"
                          data-testid={`consent-${c.id}`}
                        >
                          <span className="font-bold uppercase tracking-[0.1em] text-[10px] text-ink/55">
                            {c.kind}
                          </span>
                          <span className="font-semibold text-ink">{c.value}</span>
                          <span className="text-ink/55">·</span>
                          <span className="text-ink/65">{new Date(c.created_at).toLocaleString()}</span>
                          {c.origin && (
                            <span className="text-ink/55">· via {c.origin}</span>
                          )}
                          {c.document_version && (
                            <span className="text-ink/55">· v{c.document_version}</span>
                          )}
                          {c.ip && (
                            <span className="font-mono text-[10px] text-ink/45">· {c.ip}</span>
                          )}
                          {c.download_query && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const name = await downloadAuthenticatedPdf(
                                    api,
                                    `/admin/data/users/${userId}/executed-agreement.pdf?${c.download_query}`,
                                    "executed-agreement.pdf",
                                  );
                                  toast.success(`Downloaded ${name}`);
                                } catch (e) {
                                  toast.error(formatApiError(e, "Couldn't download executed PDF."));
                                }
                              }}
                              className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-full border border-gold/50 bg-gold/10 hover:bg-gold/20 text-[10px] font-semibold text-gold-dark transition-colors"
                              data-testid={`consent-download-${c.id}`}
                              title="Download a personalized PDF receipt of this acceptance, with the full agreement body appended."
                            >
                              <DownloadSimple size={11} weight="bold" />
                              Executed PDF
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </section>



            {/* Active subscriptions */}
            <section data-testid="admin-user-detail-subscriptions">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Gift size={14} weight="duotone" className="text-ink/65" />
                  <h3 className="font-semibold text-ink text-[14px]">
                    Subscriptions ({data.active_subscriptions?.length || 0} active)
                  </h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setGrantOpen((v) => !v)}
                  className="rounded-full h-7 text-[11.5px] border-stone-300"
                  data-testid="admin-user-grant-toggle"
                >
                  {grantOpen ? "Cancel" : "Grant comp plan"}
                </Button>
              </div>

              {grantOpen && (
                <GrantSubscriptionInline
                  userId={userId}
                  onDone={() => {
                    setGrantOpen(false);
                    refresh();
                  }}
                />
              )}

              {data.active_subscriptions?.length === 0 && (
                <p className="text-[12px] text-ink/55 italic">No active subscriptions.</p>
              )}
              <ul className="space-y-2">
                {data.active_subscriptions?.map((s) => (
                  <SubscriptionRow key={s.id} sub={s} userId={userId} onRefresh={refresh} />
                ))}
              </ul>
              {data.expired_subscriptions?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-[11px] text-ink/55 cursor-pointer">
                    {data.expired_subscriptions.length} expired
                  </summary>
                  <ul className="mt-1.5 space-y-1 pl-2">
                    {data.expired_subscriptions.map((s) => (
                      <li key={s.id} className="text-[11.5px] text-ink/55 font-tabular">
                        {s.plan} ({s.package_id}) · expired {formatDate(s.expires_at)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            {/* Recent payments */}
            <section data-testid="admin-user-detail-payments">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard size={14} weight="duotone" className="text-ink/65" />
                <h3 className="font-semibold text-ink text-[14px]">
                  Recent payments ({data.recent_payments?.length || 0})
                </h3>
              </div>
              {/* When the user has an active subscription, hoist that
                  fact ABOVE the payment table so an admin doesn't get
                  spooked by a long tail of "Abandoned" checkout
                  sessions. Stripe Checkout sessions expire 24h after
                  creation if not completed — totally normal user
                  behavior (closes the tab, comes back tomorrow). */}
              {data.active_subscriptions?.length > 0 && (
                <div
                  className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-[12px] text-emerald-900"
                  data-testid="admin-user-active-sub-banner"
                >
                  <strong>{data.active_subscriptions[0].plan}</strong> plan active through{" "}
                  <strong>{formatDate(data.active_subscriptions[0].expires_at)}</strong>
                  {" — "}
                  historical checkout sessions below are the user's full activity log,
                  not failed charges.
                </div>
              )}
              {data.recent_payments?.length === 0 && (
                <p className="text-[12px] text-ink/55 italic">No payment history.</p>
              )}
              <div className="rounded-xl border border-stone-200 overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead className="bg-stone-50 text-ink/65">
                    <tr>
                      <th className="text-left p-2 font-medium">When</th>
                      <th className="text-left p-2 font-medium">Package</th>
                      <th className="text-right p-2 font-medium">Amount</th>
                      <th className="text-left p-2 font-medium">Status</th>
                      <th className="text-right p-2 font-medium">Stripe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_payments?.map((p) => {
                      // payment_transactions stores the dollar amount as
                      // `amount` (set at checkout-session creation).
                      // Older legacy rows used `amount_usd`. Read both.
                      const amt = Number(p.amount ?? p.amount_usd ?? 0);
                      // Map Stripe Checkout Session lifecycle status +
                      // payment_status into one human label. Without
                      // this, admins panic when they see 6 "expired"
                      // rows for a user who is actually fully paid.
                      const sessionStatus = p.status;
                      const paid =
                        p.payment_status === "paid" ||
                        p.finalized === true ||
                        sessionStatus === "complete";
                      const refunded = !!p.refunded_at;
                      let label, tone;
                      if (refunded) { label = "Refunded"; tone = "bg-rose-100 text-rose-800"; }
                      else if (paid) { label = "Paid"; tone = "bg-emerald-100 text-emerald-800"; }
                      else if (sessionStatus === "expired") { label = "Abandoned"; tone = "bg-stone-100 text-ink/60"; }
                      else if (sessionStatus === "open") { label = "In progress"; tone = "bg-amber-100 text-amber-900"; }
                      else if (sessionStatus === "succeeded") { label = "Paid"; tone = "bg-emerald-100 text-emerald-800"; }
                      else { label = sessionStatus || "—"; tone = "bg-stone-100 text-ink"; }
                      return (
                        <tr key={p.id} className="border-t border-stone-100">
                          <td className="p-2 text-ink/65 font-tabular">{formatDate(p.created_at)}</td>
                          <td className="p-2">{p.package_id || "—"}</td>
                          <td className="p-2 text-right font-tabular">
                            ${amt.toFixed(2)}
                            {refunded && (
                              <span className="block text-[10px] text-rose-700">
                                -${(p.refund_amount_usd ?? amt).toFixed(2)} refunded
                              </span>
                            )}
                          </td>
                          <td className="p-2">
                            <Badge
                              className={`text-[10px] ${tone}`}
                              title={
                                label === "Abandoned"
                                  ? "Stripe Checkout session expired after 24h without payment — no charge was made."
                                  : label === "In progress"
                                  ? "Checkout session is still open — user may complete it within 24h."
                                  : label === "Paid"
                                  ? "Payment completed and fulfillment applied."
                                  : ""
                              }
                            >
                              {label}
                            </Badge>
                          </td>
                          <td className="p-2 text-right">
                            {p.stripe_payment_intent_id && (
                              <a
                                href={`https://dashboard.stripe.com/payments/${p.stripe_payment_intent_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] text-ink/55 hover:text-ink inline-flex items-center gap-0.5"
                                title="Open in Stripe to refund"
                              >
                                Refund <ArrowSquareOut size={10} />
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10.5px] text-ink/45 italic mt-2">
                <strong>Paid</strong> = charged successfully. <strong>Abandoned</strong> = the user opened the Stripe checkout page but didn't complete it within 24h (no money charged). Refunds happen in Stripe directly — click the link to open the payment.
              </p>
            </section>

            {/* Quick stats */}
            <section className="grid grid-cols-2 gap-3 text-[12px]">
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-3">
                <p className="text-ink/55 mb-0.5">Dogs registered</p>
                <p className="font-spectral text-2xl text-ink">{data.dog_count}</p>
              </div>
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-3">
                <p className="text-ink/55 mb-0.5">Litters</p>
                <p className="font-spectral text-2xl text-ink">{data.litter_count}</p>
              </div>
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionRow({ sub, userId, onRefresh }) {
  const [extendDays, setExtendDays] = useState(30);
  const [extendReason, setExtendReason] = useState("");
  const [extending, setExtending] = useState(false);
  const [showExtend, setShowExtend] = useState(false);

  const dogsDisplay =
    sub.dogs_limit === -1
      ? "unlimited dogs"
      : `${sub.dogs_used ?? 0}/${sub.dogs_limit ?? 0} dogs`;

  const submitExtend = async () => {
    if (!extendReason.trim() || extendReason.trim().length < 3) {
      toast.error("Reason is required (audit log).");
      return;
    }
    setExtending(true);
    try {
      await api.post(
        `/admin/data/users/${userId}/subscription/${sub.id}/extend?days=${extendDays}&reason=${encodeURIComponent(extendReason)}`,
      );
      toast.success(`Extended by ${extendDays} days.`);
      setShowExtend(false);
      setExtendReason("");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't extend subscription."));
    } finally {
      setExtending(false);
    }
  };

  return (
    <li className="rounded-xl border border-stone-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-ink text-[13px]">
            {sub.plan}{" "}
            <span className="text-[11px] text-ink/55 font-normal">· {sub.package_id}</span>
          </p>
          <p className="text-[11.5px] text-ink/65">
            {dogsDisplay} · expires {formatDate(sub.expires_at)}
            {sub.cancel_at_period_end && (
              <span className="ml-2 text-amber-700">· cancels at period end</span>
            )}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowExtend((v) => !v)}
          className="h-7 rounded-full text-[11px] border-stone-300"
        >
          <Clock size={11} className="mr-1" />
          {showExtend ? "Cancel" : "Extend"}
        </Button>
      </div>
      {showExtend && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input
            type="number"
            min={1}
            max={365}
            value={extendDays}
            onChange={(e) => setExtendDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
            placeholder="Days"
            className="h-9 text-[12.5px]"
          />
          <Input
            value={extendReason}
            onChange={(e) => setExtendReason(e.target.value)}
            placeholder="Reason (audit-logged)"
            className="h-9 text-[12.5px] sm:col-span-1"
          />
          <Button
            size="sm"
            disabled={extending}
            onClick={submitExtend}
            className="h-9 rounded-full text-[12px] bg-ink text-white"
          >
            {extending ? "Extending…" : `Add ${extendDays} days`}
          </Button>
        </div>
      )}
    </li>
  );
}

// Packages an admin can credit a user with from the inline grant flow.
// Plan-like SKUs create a real subscription row; one-time SKUs only log
// an offline payment_transactions row so revenue ledgers stay accurate.
const GRANT_PACKAGES_PLANS = [
  // --- V2 (current public pricing — give to NEW grants) ---
  { id: "basic_plan_v2", label: "Breeder Basic — monthly ($19) · V2" },
  { id: "basic_plan_v2_annual", label: "Breeder Basic — annual ($190) · V2" },
  { id: "pro_plan_v2", label: "Breeder Pro — monthly ($44) · V2" },
  { id: "pro_plan_v2_annual", label: "Breeder Pro — annual ($420) · V2" },
  { id: "pro_plan_v2_2yr", label: "Breeder Pro — 2-year prepay ($789) · V2 + Founding Kennel" },
  { id: "kennel_elite_v2", label: "Kennel Elite — monthly ($129) · V2" },
  { id: "kennel_elite_v2_annual", label: "Kennel Elite — annual ($1290) · V2" },
  { id: "kennel_elite_v2_2yr", label: "Kennel Elite — 2-year prepay ($2499) · V2 + Founding Kennel" },
  // --- V1 (legacy — only use to re-grant a grandfathered user the
  // same SKU they originally bought; new admin grants should use V2) ---
  { id: "basic_plan", label: "Breeder Basic — monthly ($14.99) · LEGACY" },
  { id: "basic_plan_annual", label: "Breeder Basic — annual ($129) · LEGACY" },
  { id: "pro_plan", label: "Breeder Pro — monthly ($29.99) · LEGACY" },
  { id: "pro_plan_annual", label: "Breeder Pro — annual ($249) · LEGACY" },
  { id: "kennel_elite", label: "Kennel Elite — monthly ($99) · LEGACY" },
  { id: "kennel_elite_annual", label: "Kennel Elite — annual ($899) · LEGACY" },
  // --- Non-breeder tiers (unchanged) ---
  { id: "buyer_pass_monthly", label: "Buyer Pass — monthly ($4.99)" },
  { id: "buyer_pass_annual", label: "Buyer Pass — annual ($39)" },
  { id: "care_plus_monthly", label: "Dog Log — monthly ($9.99)" },
  { id: "care_plus_annual", label: "Dog Log — annual ($69)" },
  { id: "elite_verified_annual", label: "Elite Verified Breeder — annual ($199)" },
  { id: "storefront_monthly", label: "The Dog Shop — monthly ($19)" },
  { id: "storefront_annual", label: "The Dog Shop — annual ($179)" },
];

const GRANT_PACKAGES_ONE_TIME = [
  { id: "dog_registration", label: "Registered a dog ($25)" },
  { id: "litter_registration", label: "Registered a litter ($75)" },
  { id: "certificate_purchase", label: "Pedigree certificate ($14.99)" },
  { id: "kennel_verification", label: "Verified Kennel badge ($79)" },
  { id: "transfer_ownership", label: "Ownership transfer ($15)" },
  { id: "breeding_listing_30d", label: "Breeding listing — 30 days ($14.99)" },
  { id: "breeding_listing_90d", label: "Breeding listing — 90 days ($29.99)" },
  { id: "breeding_listing_1y", label: "Breeding listing — 1 year ($49.99)" },
  { id: "dog_marketplace_listing", label: "Dog marketplace listing ($9.99)" },
  { id: "litter_marketplace_listing", label: "Litter marketplace listing ($19.99)" },
  { id: "marketplace_feature", label: "Featured marketplace listing ($29)" },
  { id: "video_tour", label: "Live video tour ($35)" },
  { id: "storefront_featured_monthly", label: "Featured Dog Shop rotation ($29)" },
];

const ONE_TIME_IDS = new Set(GRANT_PACKAGES_ONE_TIME.map((p) => p.id));

function GrantSubscriptionInline({ userId, onDone }) {
  const [pkg, setPkg] = useState("pro_plan");
  const [days, setDays] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Default mode is OFFLINE PAID — protects against accidentally
  // diluting revenue data, which was the user's #1 ask. Flip the
  // toggle for a true free comp.
  const [mode, setMode] = useState("paid"); // "paid" | "comp"
  const [paidAmount, setPaidAmount] = useState("");

  const isOneTime = ONE_TIME_IDS.has(pkg);
  // One-time SKUs can't be "comp'd" via this surface — they only make
  // sense as a logged offline payment. Force-flip the mode if a comp
  // user picks a one-time SKU.
  useEffect(() => {
    if (isOneTime && mode !== "paid") setMode("paid");
  }, [isOneTime, mode]);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason is required (audit log).");
      return;
    }
    if (mode === "paid") {
      const n = Number(paidAmount);
      if (!paidAmount || !Number.isFinite(n) || n <= 0) {
        toast.error("Enter the dollar amount the customer paid.");
        return;
      }
    }
    setBusy(true);
    try {
      const body = { package_id: pkg, reason };
      if (days && Number(days) > 0) body.days_override = Number(days);
      if (mode === "paid") body.paid_amount_usd = Number(paidAmount);
      const r = await api.post(`/admin/data/users/${userId}/grant-subscription`, body);
      const amt = Number(paidAmount).toFixed(2);
      toast.success(
        r.data.one_time
          ? `Recorded $${amt} offline payment for one-time charge.`
          : r.data.offline_payment
          ? `Recorded $${amt} offline payment — sub active.`
          : "Comp subscription granted (no revenue recorded).",
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't grant subscription."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl bg-amber-50/60 border border-amber-200 p-3 mb-3 space-y-2">
      <p className="text-[11px] text-amber-900 leading-snug">
        <strong>Offline paid</strong> records that the customer paid via
        cash / check / wire / "make whole" credit — counts as revenue and
        creates a payment_transactions row. Use this for subscriptions{" "}
        <em>or</em> one-time charges they paid you outside Stripe (e.g.{" "}
        <strong>Registered a dog</strong> for $25 in cash).{" "}
        <strong>Comp</strong> is free — doesn't count as revenue and only
        applies to subscriptions. Audit-logged either way.
      </p>

      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-1.5" data-testid="grant-mode-toggle">
        <button
          type="button"
          onClick={() => setMode("paid")}
          className={`h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
            mode === "paid"
              ? "bg-ink text-white"
              : "bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"
          }`}
          data-testid="grant-mode-paid"
        >
          Offline paid
        </button>
        <button
          type="button"
          onClick={() => !isOneTime && setMode("comp")}
          disabled={isOneTime}
          title={isOneTime ? "One-time SKUs can only be recorded as offline-paid." : ""}
          className={`h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
            mode === "comp"
              ? "bg-ink text-white"
              : "bg-white border border-stone-300 text-stone-700 hover:bg-stone-100"
          } ${isOneTime ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid="grant-mode-comp"
        >
          Comp (free)
        </button>
      </div>

      <Select value={pkg} onValueChange={setPkg}>
        <SelectTrigger className="h-9 text-[12.5px] bg-white" data-testid="grant-package-trigger">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-[0.12em] text-ink/55">
              Subscriptions
            </SelectLabel>
            {GRANT_PACKAGES_PLANS.map((p) => (
              <SelectItem key={p.id} value={p.id} className="text-[12.5px]">
                {p.label}
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-[0.12em] text-ink/55 mt-1">
              One-time charges (offline paid only)
            </SelectLabel>
            {GRANT_PACKAGES_ONE_TIME.map((p) => (
              <SelectItem key={p.id} value={p.id} className="text-[12.5px]">
                {p.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      {mode === "paid" && (
        <Input
          type="number"
          min={0}
          step="0.01"
          value={paidAmount}
          onChange={(e) => setPaidAmount(e.target.value)}
          placeholder="Amount paid in USD (e.g. 24.99)"
          className="h-9 text-[12.5px] bg-white"
          data-testid="grant-paid-amount"
        />
      )}

      {!isOneTime && (
        <Input
          type="number"
          min={1}
          max={3650}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          placeholder="Days override (optional — defaults to package days)"
          className="h-9 text-[12.5px] bg-white"
        />
      )}
      <Textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={
          mode === "paid"
            ? "How did they pay? (cash / check / wire / make-whole credit, etc.) — audit-logged"
            : "Reason for the comp (audit-logged)"
        }
        rows={2}
        className="text-[12.5px] bg-white"
      />
      <Button
        size="sm"
        disabled={busy}
        onClick={submit}
        className="rounded-full h-9 text-[12px] bg-ink text-white w-full"
        data-testid="admin-user-grant-submit"
      >
        {busy
          ? mode === "paid"
            ? "Recording…"
            : "Granting…"
          : mode === "paid"
          ? "Record offline payment"
          : "Grant comp subscription"}
      </Button>
    </div>
  );
}

function SnapStat({ label, value }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.1em] text-ink/55 font-semibold">{label}</p>
      <p className="text-[15px] font-spectral mt-0.5">{value}</p>
    </div>
  );
}

